import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Layout content already pads 16px; the page adds none of its own.
export const PageWrap = styled.div`
  padding: 0;
`;

export const FloatingFilters = styled.div`
  position: fixed;
  right: 20px;
  /* Layout footer'ının (~38px) üstünde kalır */
  bottom: 56px;
  z-index: 120;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #E5EAF0;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(0, 66, 66, 0.16);
  padding: 8px 10px;

  > div {
    width: 165px;
  }

  @media (max-width: 768px) {
    left: 12px;
    right: 12px;
    justify-content: space-between;

    > div {
      flex: 1;
      width: auto;
    }
  }
`;

export const FilterIcon = styled.span`
  display: flex;
  align-items: center;
  color: #A0A8B4;
  flex-shrink: 0;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(6, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  padding: 20px;
  grid-column: span ${({ $span }) => $span || 4};
  animation: ${fadeIn} 0.4s ease-out;
  animation-fill-mode: both;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;

  @media (max-width: 1200px) {
    grid-column: span ${({ $spanMd }) => $spanMd || 6};
  }
  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
`;

export const CardTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const CardMeta = styled.span`
  font-size: 12px;
  color: #6B7280;
  white-space: nowrap;
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const ExportBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #A0A8B4;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(2, 145, 239, 0.08);
    color: ${({ theme }) => theme.colors.primary.main};
  }
`;

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 120px;
  color: #9CA3AF;
  font-size: 13px;
  text-align: center;
`;

export const Skeleton = styled.div`
  flex: 1;
  min-height: ${({ $h }) => $h || '120px'};
  border-radius: 6px;
  background: linear-gradient(90deg, #F1F4F8 25%, #E9EEF4 37%, #F1F4F8 63%);
  background-size: 400% 100%;
  animation: shimmer 1.2s ease infinite;

  @keyframes shimmer {
    0% { background-position: 100% 0; }
    100% { background-position: 0 0; }
  }
`;

// --- KPI tiles -------------------------------------------------------------
export const KpiCard = styled(Card)`
  border-left: 4px solid ${({ $accent }) => $accent || '#2a78d6'};
  padding: 16px 18px;
  gap: 6px;
  grid-column: span ${({ $span }) => $span || 2};

  @media (max-width: 1200px) {
    grid-column: span 2;
  }
  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

export const KpiLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6B7280;
`;

export const KpiValue = styled.div`
  font-size: 30px;
  font-weight: 800;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const KpiSub = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #6B7280;

  svg { flex-shrink: 0; }
`;

// --- shared list bits --------------------------------------------------------
export const ListRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px solid #F0F2F5;

  &:last-child { border-bottom: none; }
`;

export const InitialsAvatar = styled.div`
  width: ${({ $size }) => $size || 34}px;
  height: ${({ $size }) => $size || 34}px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg || '#E8F4FD'};
  color: ${({ $color }) => $color || '#0270BA'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

// Fotoğraflı hâl — kart satırında profil resmi varsa baş harflerin YERİNE bu
// çizilir (kural: kullanıcı listelenen her yerde avatar). Ölçü/biçim
// InitialsAvatar ile birebir aynı, böylece satır hizası değişmez.
export const RowPhoto = styled.img`
  width: ${({ $size }) => $size || 34}px;
  height: ${({ $size }) => $size || 34}px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #E8F4FD;
`;

export const RowMain = styled.div`
  flex: 1;
  min-width: 0;
`;

export const RowName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RowSub = styled.div`
  font-size: 12px;
  color: #6B7280;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RowSide = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

export const TypeChip = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  color: #fff;
  background: ${({ $bg }) => $bg || '#2a78d6'};
  white-space: nowrap;
`;

export const MoreLink = styled.button`
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary.main};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 8px 0 0;
  text-align: left;

  &:hover { text-decoration: underline; }
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  font-size: 11px;
  color: #6B7280;
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background: ${({ $color }) => $color};
    ${({ $hatched }) => $hatched && `
      background: repeating-linear-gradient(45deg, #eda100 0 2px, #fff 2px 4px);
      border: 1px solid #eda100;
    `}
  }
`;

export const Footnote = styled.div`
  margin-top: 10px;
  font-size: 11px;
  color: #9CA3AF;
`;
